import {
	DocumentStatus,
	ProjectStatus,
	ProjectType,
	ValidationStatus,
	type ProjectSaleableServiceGroup,
	type ProjectSaleableService as ServerProjectSaleableService,
} from "@repo/server-api";
import { type SetStateAction } from "react";
import type { IdWithName } from "@repo/shared/common";

export type BudgetStatus = {
	totalBudgetAmount: number;
	totalBudgetMinutes: number;
	spentAmount: number;
	spentMinutes: number;
	remainingAmount: number;
	remainingMinutes: number;
	utilizationPercent: number;
	openMinutes: number;
	pendingMinutes: number;
	approvedMinutes: number;
};

export const emptyBudgetStatus: BudgetStatus = {
	totalBudgetAmount: 0,
	totalBudgetMinutes: 0,
	spentAmount: 0,
	spentMinutes: 0,
	remainingAmount: 0,
	remainingMinutes: 0,
	utilizationPercent: 0,
	openMinutes: 0,
	pendingMinutes: 0,
	approvedMinutes: 0,
};

export function calculateTotalPrice(position: ProjectPosition): number | null {
	if (position.hours != null && position.pricePerHour != null) {
		// Round to 2 decimals
		return Math.round(position.hours * position.pricePerHour * 100) / 100;
	}

	return null;
}

export type ProjectTimeSummary = {
	id: string;
	projectName: string;
	positionName: string;
	totalMinutes: number;
	workedMinutes: number;
};

export type ProjectPosition = {
	id: string;
	service: ProjectSaleableService | null;
	hours: number | null;
	pricePerHour: number | null;
	price: number | null;
	priceRate: string | null;
	totalPrice: number | null;
	description: string | null;
	accountingProfile: IdWithName | null;
};

export const createEmptyPosition = (): ProjectPosition => ({
	id: "",
	service: null,
	hours: null,
	pricePerHour: null,
	price: null,
	priceRate: null,
	totalPrice: null,
	description: "",
	accountingProfile: null,
});

export type WorksOnPosition = {
	positionId: string;
	pricePerHour: number | null;
	hours: number | null;
};

export type ProjectTeamMember = {
	id: string | null;
	teamMember: SelectedEmployee | null;
	contractor: IdWithName | null;
	worksOnPositions: WorksOnPosition[];
};

export const createEmptyTeamMember = (): ProjectTeamMember => ({
	id: "",
	teamMember: null,
	contractor: null,
	worksOnPositions: [],
});

export type ProjectSaleableService = IdWithName & {
	price: number;
};

export type SelectedEmployee = IdWithName & {
	isFreelancer: boolean;
	pricePerHour: number;
};

export type SelectedContact = IdWithName;

export type SelectedPosition = IdWithName;

export type ProjectContactInformation = {
	id: string | null;
	client: SelectedContact | null;
	contractor: SelectedContact | null;
	endCustomer: string | null;
};

export type TemplateInformation = {
	id: string | null;
	isInvoiceWithTimesheet: boolean;
	isDetailTimesheet: boolean;
};

export type ProjectContactPersonInformation = {
	id: string | null;
	clientContactPerson: SelectedContact | null;
	contractorContactPerson: SelectedContact | null;
	contractorProjectLeader: SelectedContact | null;
};

export type ProjectAccountingInformation = {
	id: string | null;
	accountingProfile: IdWithName | null;
	clientContractNumber: string | null;
	clientContractDate: string | null;
	clientOrderingNumber: string | null;
	clientOrderingDate: string | null;
	clientProjectNumber: string | null;
	supplierProjectNumber: string | null;
	provision: number | null;
	provisionDescription: string | null;
	timeTrackingResolution: number | null;
};

export type NewProject = {
	name: string;
	startDate: string | null;
	endDate: string | null;
	type: ProjectType;

	parentProject: IdWithName | null;
	contractor: SelectedContact | null;
	client: SelectedContact | null;
};

export const createEmptyNewProject = (): NewProject => ({
	name: "",
	startDate: "",
	endDate: "",
	type: ProjectType.Project,
	parentProject: null,
	contractor: null,
	client: null,
});

export type Project = {
	id: string;
	name: string;
	number: string;
	status: DocumentStatus;
	projectType: ProjectType;
	startDate: string | null;
	endDate: string | null;
	isDeleted: boolean;

	parentProject: IdWithName | null;

	contactInformation: ProjectContactInformation;
	contactPersonInformation: ProjectContactPersonInformation;
	accountingInformation: ProjectAccountingInformation;
	TemplateInformation: TemplateInformation;

	budgetHours: number;
	hasBudgetHours: boolean;
	budgetPrice: number;
	hasBudgetPrice: boolean;

	positions: ProjectPosition[];
	teamMembers: ProjectTeamMember[];

	saleableServiceGroups: ProjectSaleableServiceGroup[];
	saleableServices: ServerProjectSaleableService[];
	saleableUnits: IdWithName[];
};

export type ProjectSummary = {
	id: string;
	name: string;
	number: string;
	path: string[];
	joinedPath: string;
	status: DocumentStatus | ProjectStatus;
	isManualStatusOverride: boolean;
	projectType: ProjectType;
	validationStatus: ValidationStatus;
	startDate: string | null;
	endDate: string | null;
	isDeleted: boolean;
	type: "group" | "leaf";
	isGroup: boolean;

	parentProject: IdWithName | null;

	contactInformation: ProjectContactInformation;

	budgetHours: number;
	hasBudgetHours: boolean;
	budgetPrice: number;
	hasBudgetPrice: boolean;

	budgetStatus: BudgetStatus;
};

export const createEmptyProject = (): Project => ({
	id: "",
	name: "",
	number: "",
	status: DocumentStatus.Draft,
	projectType: ProjectType.Project,
	startDate: null,
	endDate: null,
	isDeleted: false,
	parentProject: null,

	budgetHours: 0,
	hasBudgetHours: false,
	budgetPrice: 0,
	hasBudgetPrice: false,

	positions: [],
	teamMembers: [],
	contactInformation: {
		id: "",
		client: null,
		contractor: null,
		endCustomer: null,
	},
	contactPersonInformation: {
		id: "",
		clientContactPerson: null,
		contractorContactPerson: null,
		contractorProjectLeader: null,
	},

	TemplateInformation: {
		id: null,
		isInvoiceWithTimesheet: false,
		isDetailTimesheet: false,
	},

	accountingInformation: {
		id: null,
		accountingProfile: null,
		timeTrackingResolution: 1,
		provision: null,
		provisionDescription: null,
		clientOrderingNumber: null,
		clientOrderingDate: null,
		clientContractNumber: null,
		clientContractDate: null,
		clientProjectNumber: null,
		supplierProjectNumber: null,
	},
	saleableServiceGroups: [],
	saleableServices: [],
	saleableUnits: [],
});

export const updateField = <TModel, K extends keyof TModel>(
	setProject: (value: SetStateAction<TModel>) => void,
	key: K,
	value: TModel[K],
) => {
	setProject((prev: TModel) => ({ ...prev, [key]: value }));
};

export const updateProjectField = <K extends keyof Project>(
	setProject: (value: SetStateAction<Project>) => void,
	key: K,
	value: Project[K],
) => {
	setProject((prev: Project) => ({ ...prev, [key]: value }));
};
